import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Calculator, ExternalLink, AlertTriangle, CheckCircle, Info } from "lucide-react";

interface CalculationResultProps {
  result: {
    totalSubsidy: number;
    breakdown: {
      baseAmount: number;
      unitAmount: number;
      bonusAmount: number;
      biobased: number;
    };
    warnings: string[];
    measure: any;
  } | null;
  calculationData: any;
  isLoading: boolean;
}

export const CalculationResult = ({ result, calculationData, isLoading }: CalculationResultProps) => {
  
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3">Subsidie wordt berekend...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card className="border-destructive/20 bg-destructive/5">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <div>
              <h4 className="font-medium text-destructive">Berekening niet mogelijk</h4>
              <p className="text-sm text-destructive/80">
                Er is een fout opgetreden bij het berekenen van uw subsidie. 
                Controleer uw invoer en probeer opnieuw.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { totalSubsidy, breakdown, warnings, measure } = result;

  return (
    <div className="space-y-6">
      {/* Main Result */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-6 w-6 text-primary" />
            Uw geschatte ISDE subsidie
          </CardTitle>
          <CardDescription>
            Berekening op basis van de officiële RVO tarieven 2025
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="text-4xl font-bold text-primary mb-2">
              €{totalSubsidy.toFixed(0)}
            </div>
            <p className="text-muted-foreground">
              Voor uw {calculationData.category} ({calculationData.type})
            </p>
            
            {calculationData.value > 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                {calculationData.value} {measure?.unit} • {calculationData.energyLabel && `${calculationData.energyLabel} label`}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calculation Breakdown */}
      {totalSubsidy > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Berekening details</CardTitle>
            <CardDescription>
              Zo is uw subsidie opgebouwd
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {breakdown.baseAmount > 0 && (
              <div className="flex justify-between items-center">
                <span>Basisbedrag</span>
                <span className="font-medium">€{breakdown.baseAmount.toFixed(0)}</span>
              </div>
            )}
            
            {breakdown.unitAmount > 0 && (
              <div className="flex justify-between items-center">
                <span>{calculationData.value} {measure?.unit} × €{(breakdown.unitAmount / calculationData.value).toFixed(2)}</span>
                <span className="font-medium">€{breakdown.unitAmount.toFixed(0)}</span>
              </div>
            )}
            
            {breakdown.bonusAmount > 0 && (
              <div className="flex justify-between items-center">
                <span>A+++ energielabel bonus</span>
                <span className="font-medium text-primary">+€{breakdown.bonusAmount.toFixed(0)}</span>
              </div>
            )}
            
            {breakdown.biobased > 0 && (
              <div className="flex justify-between items-center">
                <span>Biobased isolatie bonus</span>
                <span className="font-medium text-primary">+€{breakdown.biobased.toFixed(0)}</span>
              </div>
            )}
            
            <Separator />
            
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>Totaal ISDE subsidie</span>
              <span className="text-primary">€{totalSubsidy.toFixed(0)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-orange-900">
              <AlertTriangle className="h-5 w-5" />
              Belangrijke informatie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {warnings.map((warning, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-orange-800">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {warning}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-primary" />
            Volgende stappen
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">1</div>
              <div>
                <p className="font-medium">Controleer meldcode</p>
                <p className="text-sm text-muted-foreground">Zorg dat uw product op de RVO meldcodelijst staat</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">2</div>
              <div>
                <p className="font-medium">Laat uitvoeren door installateur</p>
                <p className="text-sm text-muted-foreground">Bewaar factuur, betaalbewijs en foto's</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">3</div>
              <div>
                <p className="font-medium">Dien subsidieaanvraag in</p>
                <p className="text-sm text-muted-foreground">Binnen 24 maanden via RVO met DigiD</p>
              </div>
            </div>
          </div>
          
          <Separator />
          
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" asChild>
              <a href="https://www.rvo.nl/subsidies-financiering/isde/woningeigenaren" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                RVO Stappenplan
              </a>
            </Button>
            
            <Button variant="outline" size="sm" asChild>
              <a href="https://www.rvo.nl/subsidies-financiering/isde/meldcodelijsten" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Meldcodelijst
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="border-muted">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            <strong>Disclaimer:</strong> Dit is een schatting op basis van de huidige ISDE tarieven 2025. 
            De werkelijke subsidie kan afwijken. Controleer altijd de actuele bedragen op RVO.nl en 
            vraag uw installateur naar de exacte meldcode van uw product.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};